import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * Compute stats for a checklist
 */
function computeStats(checklist) {
    if (!checklist) return { ok: 0, notOk: 0, total: 0 };
    const { templateSnapshot, responses, stage } = checklist;
    let ok = 0, notOk = 0, total = 0;
    if (templateSnapshot?.sections && responses) {
        for (const section of templateSnapshot.sections) {
            if (!section.stages.includes(stage)) continue;
            for (const item of section.items) {
                total++;
                const r = responses[section.key]?.[item.key];
                if (r?.ok === true) ok++;
                else if (r?.ok === false) notOk++;
            }
        }
    }
    return { ok, notOk, total };
}

/**
 * Table view of a single checklist section
 */
const SectionTable = ({ section, responses, isServiceTask, comparisonResponses }) => {
    const sectionResponses = responses?.[section.key] || {};
    const compSectionResponses = comparisonResponses?.[section.key] || {};
    const hasComparison = comparisonResponses !== null;

    return (
        <Accordion
            defaultExpanded
            elevation={0}
            sx={{
                mb: 1,
                '&:before': { display: 'none' },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px !important',
                overflow: 'hidden',
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    minHeight: 40,
                    bgcolor: 'grey.50',
                    '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1 },
                }}
            >
                <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
                    {section.label}
                </Typography>
                {(() => {
                    const issues = section.items.filter(i => sectionResponses[i.key]?.ok === false).length;
                    return issues > 0 ? (
                        <Chip
                            icon={<WarningIcon sx={{ fontSize: '0.85rem !important' }} />}
                            label={`${issues} issue${issues > 1 ? 's' : ''}`}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 24, fontSize: '0.7rem' }}
                        />
                    ) : (
                        <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: '0.85rem !important' }} />}
                            label="All OK"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 24, fontSize: '0.7rem' }}
                        />
                    );
                })()}
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                <TableCell sx={{ fontWeight: 600, width: '40%' }}>Item</TableCell>
                                <TableCell sx={{ fontWeight: 600, width: '15%' }} align="center">Status</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Note</TableCell>
                                {hasComparison && (
                                    <TableCell sx={{ fontWeight: 600, width: '10%' }} align="center">Changed</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {section.items.map(item => {
                                const r = sectionResponses[item.key];
                                const compR = compSectionResponses[item.key];
                                const changed = hasComparison && compR && r &&
                                    (compR.ok !== r.ok || (compR.note || '') !== (r.note || ''));

                                return (
                                    <TableRow
                                        key={item.key}
                                        sx={{
                                            bgcolor: changed ? 'warning.50' : 'inherit',
                                            '&:last-child td': { borderBottom: 0 },
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2">{item.label}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {r?.ok === true ? (
                                                <Chip
                                                    icon={<CheckCircleIcon sx={{ fontSize: '0.8rem !important' }} />}
                                                    label={isServiceTask ? 'Yes' : 'OK'}
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                                />
                                            ) : r?.ok === false ? (
                                                <Chip
                                                    icon={<CancelIcon sx={{ fontSize: '0.8rem !important' }} />}
                                                    label={isServiceTask ? 'No' : 'Not OK'}
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                                />
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {r?.note || '—'}
                                            </Typography>
                                        </TableCell>
                                        {hasComparison && (
                                            <TableCell align="center">
                                                {changed && (
                                                    <Chip
                                                        label="Changed"
                                                        size="small"
                                                        color="warning"
                                                        sx={{ height: 20, fontSize: '0.6rem' }}
                                                    />
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </AccordionDetails>
        </Accordion>
    );
};

/**
 * View of a single checklist (either START or COMPLETE)
 */
const ChecklistView = ({ checklist, comparisonChecklist }) => {
    if (!checklist) return null;

    const { templateSnapshot, responses, stage, submittedAt } = checklist;
    const sections = (templateSnapshot?.sections || []).filter(s => s.stages.includes(stage));
    const compResponses = comparisonChecklist?.responses || null;

    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Submitted: {new Date(submittedAt).toLocaleString()}
                {comparisonChecklist && ' • Showing changes from reception'}
            </Typography>
            {sections.map(section => (
                <SectionTable
                    key={section.key}
                    section={section}
                    responses={responses}
                    isServiceTask={section.key === 'service_tasks'}
                    comparisonResponses={compResponses}
                />
            ))}
        </Box>
    );
};

/**
 * Main component: Tabbed view of inspection checklists for a job card
 * Read-only, used in POS Portal's JobCardDetails
 */
const JobCardChecklistView = ({ checklists }) => {
    const [activeTab, setActiveTab] = useState(0);

    const startChecklist = useMemo(() => (checklists || []).find(c => c.stage === 'START'), [checklists]);
    const completeChecklist = useMemo(() => (checklists || []).find(c => c.stage === 'COMPLETE'), [checklists]);
    const available = [startChecklist, completeChecklist].filter(Boolean);

    if (available.length === 0) return null;

    const startStats = computeStats(startChecklist);
    const completeStats = computeStats(completeChecklist);

    return (
        <Box>
            {available.length > 1 ? (
                <>
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        sx={{ mb: 2 }}
                    >
                        <Tab
                            label={
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <span>Reception</span>
                                    <Chip label={`${startStats.ok} OK`} size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                    {startStats.notOk > 0 && (
                                        <Chip label={`${startStats.notOk} Issues`} size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                    )}
                                </Stack>
                            }
                            sx={{ textTransform: 'none' }}
                        />
                        <Tab
                            label={
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <span>Completion</span>
                                    <Chip label={`${completeStats.ok} OK`} size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                    {completeStats.notOk > 0 && (
                                        <Chip label={`${completeStats.notOk} Issues`} size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                    )}
                                </Stack>
                            }
                            sx={{ textTransform: 'none' }}
                        />
                    </Tabs>
                    {activeTab === 0 && <ChecklistView checklist={startChecklist} />}
                    {activeTab === 1 && (
                        <ChecklistView
                            checklist={completeChecklist}
                            comparisonChecklist={startChecklist}
                        />
                    )}
                </>
            ) : (
                <>
                    <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                        <Chip
                            label={available[0].stage === 'START' ? 'Reception' : 'Completion'}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    </Stack>
                    <ChecklistView checklist={available[0]} />
                </>
            )}
        </Box>
    );
};

export default JobCardChecklistView;
